import { CheckCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/solid'
import Badge from 'components/base/Badge'
import Tooltip from 'components/base/Tooltip'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import type { PromoCode } from 'lib/dto'
import { useIntl } from 'lib/intl-utils'
import { useState } from 'react'
import { XIcon } from '@heroicons/react/outline'

type Props = {
  promoCode: PromoCode
  alwaysShortText?: boolean
  compact?: boolean
  badgeClassName?: string
  onRemove?: () => void
}

const PromoCode: FC<Props> = ({
  promoCode,
  alwaysShortText = false,
  compact = false,
  badgeClassName,
  onRemove,
}) => {
  const ctx = useUserContext()
  const { t, format } = useIntl()
  const [open, setOpen] = useState(false)

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  return (
    <Tooltip
      rootProps={{ open }}
      sizing="custom"
      className="px-3 py-3 rounded-lg"
      disabled={!promoCode}
      content={
        <div className="">
          <p className="mb-2">
            {t`landing.promo_code`}{' '}
            <span className="font-medium tracking-wider">{promoCode?.code}</span>
          </p>
          {promoCode?.freeDays && (
            <p className="flex items-center space-x-1.5 mt-1.5">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <span>
                {promoCode.freeDays} {t('day', { count: promoCode.freeDays })}{' '}
                {t`landing.days_free`}
              </span>
            </p>
          )}
          {(promoCode?.discountMonth ||
            promoCode?.discount3months ||
            promoCode?.discount6months ||
            promoCode?.discountYear) && (
            <p className="flex items-center space-x-1.5 mt-1.5">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <span>
                {t`landing.with_promo_code`}{' '}
                {Math.max(
                  promoCode.discountMonth ?? 0,
                  promoCode.discount3months ?? 0,
                  promoCode.discount6months ?? 0,
                  promoCode.discountYear ?? 0
                )}
                % скидка
              </span>
            </p>
          )}
        </div>
      }
    >
      <div
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        className={c`w-max cursor-default rounded-full transition-opacity duration-500 ease-in ${compact} p-0 bg-transparent | px-0.5 bg-gradient-to-tr from-lime-300 to-lime-400 py-0.5 ${Boolean(
          promoCode
        )} opacity-100 | opacity-0`}
      >
        <Badge
          className={c`bg-white ring-2 ${compact} h-6 !pl-2 !pr-1 !text-xs gap-0.5 border-0 | !pl-3 !pr-1.5 gap-1 border-2 border-transparent ${ctx.theme.hostType === 'mayak'} ring-amber-300 | ring-lime-300 ${badgeClassName}`}
        >
          <p>
            <span>{t`landing.promo_code`}</span>
            <span className={c`hidden ${!alwaysShortText} sm:inline lg:hidden xl:hidden`}>
              {' '}
              {t`landing.promo_code_active`}
            </span>
          </p>
          <QuestionMarkCircleIcon
            className={c`text-gray-600 shrink-0 ${compact} size-3.5 | size-5`}
          />
          {onRemove ? (
            <button
              type="button"
              onClick={onRemove}
              className={c`flex shrink-0 items-center justify-center text-gray-400 transition-colors hover:text-gray-800 ${compact} size-3.5 | size-5`}
            >
              <XIcon className={c`${compact} size-3.5 | size-5`} />
            </button>
          ) : (
            <a
              className={c`flex shrink-0 items-center justify-center text-gray-400 transition-colors hover:text-gray-800 ${compact} size-3.5 | size-5`}
              href="/?promoCode="
            >
              <XIcon className={c`${compact} size-3.5 | size-5`} />
            </a>
          )}
        </Badge>
      </div>
    </Tooltip>
  )
}

export default PromoCode
